import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BlogSidebar from "../BlogArticle/BlogSidebar";
import "../BlogArticle/BlogArticle.css";
import "../BlogArticle/blog-gaming.css";
import "../BlogArticle/blog-layout.css";

const GRID_STEP = 6;

function thumbOf(p) {
  // Prefer the article's hero image (unique per post) over the shared promo thumbnail.
  return p?.heroImageUrl || p?.thumbnailUrl || null;
}
function dateOf(p) {
  return p?.author?.publishedDate || p?.publishedDate || p?.createdAt || p?.approvedAt || null;
}
function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const BlogIndex = ({ pages = [] }) => {
  const { t } = useTranslation();
  const sorted = [...pages].sort(
    (a, b) => new Date(dateOf(b) || 0) - new Date(dateOf(a) || 0)
  );
  // Feature the "daily login rewards" post at the top; fall back to the newest post.
  const featured =
    sorted.find((p) => (p.title || "").toLowerCase().includes("daily login")) ||
    sorted[0] ||
    null;
  const rest = sorted.filter((p) => p !== featured);

  const [shown, setShown] = useState(GRID_STEP);
  const visibleRest = rest.slice(0, shown);
  const hasMore = shown < rest.length;

  return (
    <>
      <Helmet>
        <title>{t('blog.pageTitle')}</title>
        <meta
          name="description"
          content={t('blog.metaDescription')}
        />
        <link rel="canonical" href="https://coin.spinsagacasino.com/blog" />
      </Helmet>

      <main className="ss-blog" style={{ marginTop: "70px" }}>
        <div className="ss-blog__inner">
          <div className="ss-list__head">
            <h1>{t('blog.heading')}</h1>
            <p>{t('blog.description')}</p>
          </div>

          <div className="ss-blog__layout">
            {/* Left column — featured + grid */}
            <div className="ss-blog__main">
              {pages.length === 0 && (
                <p style={{ color: "rgba(255,255,255,0.6)" }}>{t('blog.noArticles')}</p>
              )}

              {/* Featured — newest post */}
              {featured && (
                <Link to={`/blog/${featured.slug}`} className="ss-featured">
                  <span className="ss-featured__img">
                    {thumbOf(featured) && <img src={thumbOf(featured)} alt={featured.title} />}
                  </span>
                  <span className="ss-featured__body">
                    <h3>{featured.title}</h3>
                    <span className="ss-featured__meta">
                      <span>{featured.author?.name || t('blog.defaultAuthor')}</span>
                      {dateOf(featured) && <span>· {formatDate(dateOf(featured))}</span>}
                    </span>
                    <p>{featured.metaDescription}</p>
                    <span className="ss-featured__cta">{t('blog.readArticle')}</span>
                  </span>
                </Link>
              )}

              {/* Latest posts grid */}
              {rest.length > 0 && (
                <>
                  <h2 className="ss-section-title">
                    <span className="accent">{t('blog.latestPostsAccent')}</span> {t('blog.latestPostsRest')}
                  </h2>
                  <div className="ss-grid">
                    {visibleRest.map((p) => {
                      const thumb = thumbOf(p);
                      return (
                        <Link key={p.slug} to={`/blog/${p.slug}`} className="ss-card">
                          {thumb ? (
                            <img className="ss-card__img" src={thumb} alt={p.title} loading="lazy" />
                          ) : (
                            <span className="ss-card__img--ph" aria-hidden="true" />
                          )}
                          <span className="ss-card__body">
                            <span className="ss-card__flag">{p.pageType || t('blog.defaultPageType')}</span>
                            <span className="ss-card__title">{p.title}</span>
                            <span className="ss-card__excerpt">{p.metaDescription}</span>
                            <span className="ss-card__foot">
                              <span>{formatDate(dateOf(p))}</span>
                              <span className="read">{t('blog.read')}</span>
                            </span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                  {hasMore && (
                    <button
                      type="button"
                      className="ss-blog__more"
                      onClick={() => setShown((n) => n + GRID_STEP)}
                    >
                      {t('blog.seeMore')}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Right column — shared sidebar */}
            <BlogSidebar pages={pages} />
          </div>
        </div>
      </main>
    </>
  );
};

export default BlogIndex;
